import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { usePortOrders, useTelviaIQOrders } from '@/features/dids/hooks';
import type { PortOrder, PortOrderStatus, TelviaIQOrder, TelviaIQOrderStatus } from '@/features/dids/types';

// Placeholder port IDs use the range 100001-199999
// Real Sangoma port IDs are typically 6+ digits (e.g., 384457)
const PLACEHOLDER_PORT_ID_MIN = 100001;
const PLACEHOLDER_PORT_ID_MAX = 199999;

function isPlaceholderPortId(portId: number): boolean {
  return portId >= PLACEHOLDER_PORT_ID_MIN && portId < PLACEHOLDER_PORT_ID_MAX;
}

// Status badge styling based on port status
function getStatusBadge(status: PortOrderStatus | null) {
  const baseClasses = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium';

  switch (status) {
    case 'PENDING':
    case 'NEW':
      return `${baseClasses} bg-yellow-100 text-yellow-800`;
    case 'SUBMITTED':
      return `${baseClasses} bg-blue-100 text-blue-800`;
    case 'FOCd':
      return `${baseClasses} bg-purple-100 text-purple-800`;
    case 'COMPLETED':
      return `${baseClasses} bg-green-100 text-green-800`;
    case 'CANCELLED':
      return `${baseClasses} bg-gray-100 text-gray-800`;
    case 'REJECTED':
      return `${baseClasses} bg-red-100 text-red-800`;
    default:
      return `${baseClasses} bg-gray-100 text-gray-500`;
  }
}

// Status badge for TelviaIQ orders
function getActivityStatusBadge(status: TelviaIQOrderStatus) {
  const baseClasses = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium';

  switch (status) {
    case 'PENDING':
      return `${baseClasses} bg-yellow-100 text-yellow-800`;
    case 'SUCCESS':
      return `${baseClasses} bg-green-100 text-green-800`;
    case 'FAILED':
      return `${baseClasses} bg-red-100 text-red-800`;
    default:
      return `${baseClasses} bg-gray-100 text-gray-500`;
  }
}

// Order type category badge (for PURCHASE/DISCONNECT distinction)
function getOrderTypeBadge(orderType: string): { label: string; classes: string } | null {
  const baseClasses = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium';

  switch (orderType) {
    case 'DID_ORDER':
      return { label: 'PURCHASE', classes: `${baseClasses} bg-indigo-50 text-indigo-700` };
    case 'DID_RELEASE':
      return { label: 'DISCONNECT', classes: `${baseClasses} bg-slate-100 text-slate-700` };
    default:
      return null;
  }
}

// Format date for display
function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

// Format datetime for activity log
function formatDateTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return dateString;
  }
}

// Format phone number for display
function formatPhone(tn: string | null): string {
  if (!tn) return '-';
  const cleaned = tn.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return tn;
}

// Format activity details based on order type
function formatActivityDetails(activity: TelviaIQOrder): string | null {
  const { order_type, old_value, new_value } = activity;

  // No details for these types
  if (!new_value && !old_value) return null;

  switch (order_type) {
    case 'FORWARD_ENABLE':
    case 'FORWARD_UPDATE':
      return `Fwd set to ${formatPhone(new_value || null)}`;
    case 'FORWARD_DISABLE':
      return old_value ? `Fwd removed (was ${formatPhone(old_value)})` : 'Fwd removed';

    case 'FAILOVER_ENABLE':
      return `Failover set to ${formatPhone(new_value || null)}`;
    case 'FAILOVER_DISABLE':
      return old_value ? `Failover removed (was ${formatPhone(old_value)})` : 'Failover removed';

    case 'FAX_ENABLE':
      return `Email set to ${new_value}`;
    case 'FAX_DISABLE':
      return old_value ? `eFax removed (was ${old_value})` : 'eFax removed';

    case 'CONFERENCE_ENABLE':
      return `PIN: ${new_value}`;
    case 'CONFERENCE_DISABLE':
      return old_value ? `Bridge removed (PIN was ${old_value})` : 'Bridge removed';

    case 'CNAM_UPDATE':
      return `Caller ID set to "${new_value}"`;
    case 'CNAM_ENABLE':
      return new_value ? `CNAM enabled: "${new_value}"` : 'CNAM enabled';
    case 'CNAM_DISABLE':
      return 'CNAM disabled';

    case 'E911_ADD':
      return new_value ? `Address: ${new_value}` : 'E911 registered';
    case 'E911_UPDATE':
      return new_value ? `Updated to: ${new_value}` : 'E911 updated';
    case 'E911_REMOVE':
      return 'E911 removed';

    case 'E911_ALERT_ENABLE':
      return new_value ? `Alerts to: ${new_value}` : 'Alerts enabled';
    case 'E911_ALERT_DISABLE':
      return 'Alerts disabled';

    default:
      // Fallback to old → new format for any other types
      if (new_value && old_value) {
        return `${old_value} → ${new_value}`;
      }
      return new_value || null;
  }
}

// Split port orders into pending (active) and completed (history)
function splitPortOrders(orders: PortOrder[]): { pending: PortOrder[]; history: PortOrder[] } {
  const pending: PortOrder[] = [];
  const history: PortOrder[] = [];

  for (const order of orders) {
    if (order.status === 'COMPLETED' || order.status === 'CANCELLED' || order.status === 'REJECTED') {
      history.push(order);
    } else {
      pending.push(order);
    }
  }

  return { pending, history };
}

export function OrdersPage() {
  const { data: portOrders = [], isLoading: portsLoading, error: portsError } = usePortOrders();
  const { data: activityLog = [], isLoading: activityLoading, error: activityError } = useTelviaIQOrders();
  const { pending, history } = splitPortOrders(portOrders);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-display font-semibold text-slate-800">
        Orders Dashboard
      </h1>

      {/* Port Orders - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Port Orders */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Pending Port Orders</span>
              {pending.length > 0 && (
                <span className="text-sm font-normal text-slate-500">
                  {pending.length} active
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Port ID</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Carrier</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">FOC Date</th>
                  </tr>
                </thead>
                <tbody>
                  {portsLoading ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-slate-500">
                        <div className="flex items-center justify-center gap-2">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"></div>
                          Loading port orders...
                        </div>
                      </td>
                    </tr>
                  ) : portsError ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-red-500">
                        Failed to load port orders. Please try again.
                      </td>
                    </tr>
                  ) : pending.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-slate-500 italic">
                        No pending port orders.
                      </td>
                    </tr>
                  ) : (
                    pending.map((order) => (
                      <tr key={order.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-3 font-medium text-slate-800">
                          {isPlaceholderPortId(order.id) ? (
                            <span className="text-amber-600 italic">Pending</span>
                          ) : (
                            `#${order.id}`
                          )}
                        </td>
                        <td className="py-2 px-3">
                          <span className={getStatusBadge(order.status)}>
                            {isPlaceholderPortId(order.id) ? 'Pending Review' : (order.status || 'Unknown')}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-slate-600">
                          {order.porting_from || '-'}
                        </td>
                        <td className="py-2 px-3 text-slate-600">
                          {formatDate(order.foc_date)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Port Order History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Port Order History</span>
              {history.length > 0 && (
                <span className="text-sm font-normal text-slate-500">
                  {history.length} completed
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Port ID</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">BTN</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">DIDs</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {portsLoading ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-slate-500">
                        <div className="flex items-center justify-center gap-2">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"></div>
                          Loading...
                        </div>
                      </td>
                    </tr>
                  ) : portsError ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-red-500">
                        Failed to load port history.
                      </td>
                    </tr>
                  ) : history.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-slate-500 italic">
                        No completed port orders.
                      </td>
                    </tr>
                  ) : (
                    history.map((order) => (
                      <tr key={order.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-3 font-medium text-slate-800">
                          #{order.id}
                        </td>
                        <td className="py-2 px-3 text-slate-600">
                          {formatPhone(order.billing_tn)}
                        </td>
                        <td className="py-2 px-3 text-slate-600">
                          {order.did_count} number{order.did_count !== 1 ? 's' : ''}
                        </td>
                        <td className="py-2 px-3">
                          <span className={getStatusBadge(order.status)}>
                            {order.status || 'Unknown'}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-slate-600">
                          {formatDate(order.foc_date)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-slate-800">
              {portsLoading ? '-' : pending.length}
            </div>
            <div className="text-sm text-slate-500">Active Port Orders</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">
              {portsLoading ? '-' : history.filter(o => o.status === 'COMPLETED').length}
            </div>
            <div className="text-sm text-slate-500">Completed Ports</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-slate-800">
              {portsLoading ? '-' : portOrders.reduce((sum, o) => sum + o.did_count, 0)}
            </div>
            <div className="text-sm text-slate-500">Total DIDs Ported</div>
          </CardContent>
        </Card>
      </div>

      {/* Activity Orders Log - Full Width */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Activity Orders Log</span>
            {activityLog.length > 0 && (
              <span className="text-sm font-normal text-slate-500">
                {activityLog.length} records
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Date/Time</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Action</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Phone Number</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">User</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Details</th>
                </tr>
              </thead>
              <tbody>
                {activityLoading ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-slate-500">
                      <div className="flex items-center justify-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"></div>
                        Loading activity log...
                      </div>
                    </td>
                  </tr>
                ) : activityError ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-red-500">
                      Failed to load activity log. Please try again.
                    </td>
                  </tr>
                ) : activityLog.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-slate-500 italic">
                      No activity recorded yet.
                    </td>
                  </tr>
                ) : (
                  activityLog.map((activity: TelviaIQOrder) => (
                    <tr key={activity.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 text-slate-600">
                        {formatDateTime(activity.created_at)}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800">{activity.order_type_display}</span>
                          {getOrderTypeBadge(activity.order_type) && (
                            <span className={getOrderTypeBadge(activity.order_type)!.classes}>
                              {getOrderTypeBadge(activity.order_type)!.label}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-slate-600 font-mono">
                        {activity.tn ? formatPhone(activity.tn) : (
                          activity.port_dids && activity.port_dids.length > 0
                            ? `${activity.port_dids.length} numbers`
                            : '-'
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <span className={getActivityStatusBadge(activity.status)}>
                          {activity.status_display}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-slate-600">
                        {activity.username || '-'}
                      </td>
                      <td className="py-2 px-3 text-slate-600">
                        {activity.order_type === 'PORT_SUBMISSION' && activity.signer_name && (
                          <span className="text-xs">
                            Signed by {activity.signer_name}
                            {activity.signer_company && ` (${activity.signer_company})`}
                          </span>
                        )}
                        {activity.order_type !== 'PORT_SUBMISSION' && !activity.error_message && formatActivityDetails(activity) && (
                          <span className="text-xs text-slate-500">
                            {formatActivityDetails(activity)}
                          </span>
                        )}
                        {activity.error_message && (
                          <span className="text-xs text-red-600" title={activity.error_message}>
                            Error
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
